import { Position } from '@xyflow/react'
import type { ElkExtendedEdge, ElkNode, LayoutOptions } from 'elkjs/lib/elk-api'

import { SwimlaneSummary } from '@flowx/designer/modules/core/dictionary/swimlane.dictionary'
import {
  EventNodes,
  GatewayNodes,
  NodeTypesWithSystemDefinedBoundaryEvents,
} from '@flowx/designer/shared/entities/node.types'
import { SequencePoints } from '@flowx/designer/shared/entities/sequence.dictionary'
import { LayoutPoint } from '@flowx/designer/shared/react-flow/auto-layout/auto-layout.types'
import {
  layeredLayoutOptions,
  runElkLayout,
} from '@flowx/designer/shared/react-flow/auto-layout/elk-runner'
import {
  RANK_SEED_STEP,
  ROW_STRAIGHTNESS,
} from '@flowx/designer/shared/react-flow/auto-layout/row-ranks'
import { RouteBox } from '@flowx/designer/shared/react-flow/edge-routing/edge-routing.types'
import {
  NODE_CLEARANCE,
  findRectilinearRoute,
  obstaclesAround,
  scoreRoute,
} from '@flowx/designer/shared/react-flow/edge-routing/orthogonal-router'
import {
  padBox,
  simplifyRoute,
  toRouteBox,
  toSegments,
} from '@flowx/designer/shared/react-flow/edge-routing/route-geometry'

import {
  Rect,
  SEQUENCE_EDGE_TYPE,
  SequenceEdgeData,
  initialSequencePoints,
  toFlowGraph,
} from './bpmn-graph.mapper'
import { pointsEqual } from './drag-geometry.util'
import { isOrthogonalRoute, routeBetweenRectPositions } from './edges/orthogonal-edge.util'
import { getNodeDimensions } from './node-visuals'
import { branchRanks, gatewayBranchOffsets, mainPath } from './process-gateway-layout.util'

import { CanvasData, NodeDefinitionRender } from '../../bpmn-canvas.types'
import {
  GRID_SNAP_SIZE,
  SWIMLANE_DEFAULT_LAYOUT_OPTIONS,
  SWIMLANE_PADDING,
} from '../../bpmn-graph.constants'
import { Point } from '../../dictionary/geometry.types'

/** Spacing on top of the shared layered defaults, sized for 140×90 tasks and 40px events. */
export const PROCESS_LAYOUT_OPTIONS: LayoutOptions = {
  'elk.layered.spacing.nodeNodeBetweenLayers': '100',
  // Vertical gap between nodes of one column
  'elk.spacing.nodeNode': '100',
}

/** The ELK options for a process of `nodeCount` nodes. */
export const processLayoutOptions = (nodeCount: number): LayoutOptions =>
  layeredLayoutOptions(nodeCount, PROCESS_LAYOUT_OPTIONS)

/** Straightness priority of main-path sequences (and parallel split→join pairs); others are 0. */
const MAIN_PATH_STRAIGHTNESS = ROW_STRAIGHTNESS
/**
 * Vertical step between a gateway's branch ports, around its centre. Tiny on purpose: the ports
 * only have to give ELK the branch order, while the main branch stays exactly at the centre so its
 * sequence — attached at the middle of the side — is drawn straight.
 */
const GATEWAY_PORT_STEP = 0.5

/** The lane label strip is 40px wide; content starts one padding step past it. */
const LANE_CONTENT_LEFT = 40 + SWIMLANE_PADDING
const LANE_CONTENT_TOP = SWIMLANE_PADDING
const LANE_CONTENT_RIGHT = SWIMLANE_PADDING
const LANE_CONTENT_BOTTOM = SWIMLANE_PADDING
/** Same floors the lane resize handles enforce. */
const MIN_LANE_HEIGHT = 150
const MIN_LANE_WIDTH = 300
/** Initial y stride per lane: far enough apart that lane order dominates the initial order. */
const LANE_ORDER_STRIDE = 100_000
/**
 * Seeds for nodes placed by their gateway branch rank: centred in the lane's band, one rank
 * apart by more than any drawn offset so ranks, not the old drawing, decide the order.
 */
const RANK_SEED_CENTRE = LANE_ORDER_STRIDE / 2
/** Seed distance between staircase steps: well inside one rank, so they stay by their task. */
const STAIRCASE_SEED_STEP = RANK_SEED_STEP / 10

const START_TYPES: string[] = [
  EventNodes.START_EVENT,
  EventNodes.START_MESSAGE_CATCH_EVENT,
  EventNodes.START_TIMER_EVENT,
]

export interface LaneLayout {
  x: number
  y: number
  width: number
  height: number
}

export interface ProcessLayoutPlan {
  /** Lane-relative positions, the way nodes are persisted. */
  nodes: { node: NodeDefinitionRender; layoutOptions: Point }[]
  swimlanes: { swimlane: SwimlaneSummary; layoutOptions: LaneLayout }[]
  sequences: { sequence: SequencePoints; points: Point[] }[]
}

const nodeKey = (id: number): string => `node-${id}`
const portKey = (boundaryEventId: number): string => `boundary-${boundaryEventId}`
const inPortKey = (nodeId: number): string => `${nodeKey(nodeId)}::in`
const outPortKey = (nodeId: number): string => `${nodeKey(nodeId)}::out`
const snap = (value: number): number => Math.round(value / GRID_SNAP_SIZE) * GRID_SNAP_SIZE
/**
 * Snaps a node by its centre, like a drag on the canvas does: nodes of different heights (a 40px
 * event, a 90px task) then share a centre line instead of a top edge, so the sequence between them
 * — attached at the middle of each side — stays straight.
 */
const snapByCentre = (start: number, size: number): number => snap(start + size / 2) - size / 2
const laneY = (swimlane: SwimlaneSummary): number => swimlane.layoutOptions?.y ?? 0

const orderLanes = (swimlanes: SwimlaneSummary[]): SwimlaneSummary[] =>
  [...swimlanes].sort((a, b) => laneY(a) - laneY(b))

/** Boundary events drawn on the canvas; timer/message event nodes carry system-defined ones. */
const drawnBoundaryEvents = (data: CanvasData): CanvasData['boundaryEvents'] => {
  const nodesById = new Map(data.nodes.map((node) => [node.id, node]))

  return data.boundaryEvents
    .filter((boundaryEvent) => nodesById.has(boundaryEvent.nodeDefinitionId))
    .filter(
      (boundaryEvent) =>
        !NodeTypesWithSystemDefinedBoundaryEvents.includes(
          nodesById.get(boundaryEvent.nodeDefinitionId).type as EventNodes
        )
    )
}

type BoundaryEventRender = CanvasData['boundaryEvents'][number]

/** Vertical gap between a task's border and the exception children placed beside it. */
const EXCEPTION_CHILD_GAP = 60
/** Horizontal gap between exception children sharing the row beside one task. */
const EXCEPTION_CHILD_SPACING = 40
/** How far before a staircase step its exception flow turns down: half the gap between columns. */
const STAIRCASE_TURN_GAP = 50

const blockKey = (taskId: number): string => `block-${taskId}`

/** Which way from its task an exception child goes: always away from the main row. */
export type ExceptionDirection = 'up' | 'down'
export const EXCEPTION_DIRECTIONS: ExceptionDirection[] = ['down', 'up']

/** A staircase step: an exception child laid out as an ordinary node, a column and a row out. */
export interface StaircaseStep {
  nodeId: number
  /**
   * Its boundary event sits on the task's other edge (facing the main row), so the flow leaves
   * the event towards the main row and wraps round the task's right side into the step.
   */
  wraps: boolean
  /**
   * How far out (in levels) the flow runs: from the event's edge for a facing flow, beyond the
   * task's other edge for a wrapping one. Chosen so no two of a task's flows cross.
   */
  level: number
  /** A wrapping flow's level on the way back past the task's edge, closer in than any facing flow. */
  returnLevel?: number
}

/**
 * A task and the exception children placed beside it, laid out by ELK as one node so the column
 * keeps room for them. Exception children go away from the main row — a child in another lane
 * towards its lane, a task below the main row down, a task above it up, a task on the main row
 * (or one no start event reaches) the way its event's edge faces. Per direction, the first event
 * on the edge facing that way has its children in a row next to the task, each centred on the
 * event, pushed right when it would overlap its left neighbour. Offsets are relative to the
 * block's top-left corner.
 *
 * Every other child is a staircase step: one column right of and one row further out than the
 * step before it — the children of events on the facing edge first, in border order, then those
 * whose event faces the main row, right to left.
 */
export interface ExceptionBlock {
  taskId: number
  taskOffset: Point
  children: { nodeId: number; offset: Point }[]
  staircases: Record<ExceptionDirection, StaircaseStep[]>
  /** The side each boundary event's exception flows leave the block from. */
  eventDirections: Map<number, ExceptionDirection>
  width: number
  height: number
}

/**
 * Nodes that belong with a task: targets of the task's boundary flows whose every incoming
 * sequence comes from that task's boundary events, so there is no main-flow position they would
 * be pulled away from. A join target, a start event, or a node that has exception children of its
 * own (no nesting) keeps a normal place in the flow. Children are ordered by where their boundary
 * event sits along the task's border. A child may be in another lane: it goes towards that lane.
 */
export function exceptionChildrenByTask(
  data: CanvasData
): Map<number, { node: NodeDefinitionRender; boundaryEvent: BoundaryEventRender }[]> {
  const nodesById = new Map(data.nodes.map((node) => [node.id, node]))
  const boundariesById = new Map(
    drawnBoundaryEvents(data).map((boundaryEvent) => [boundaryEvent.id, boundaryEvent])
  )
  const boundaryOwner = (sequence: CanvasData['sequences'][number]): number | undefined =>
    boundariesById.get(sequence.boundaryEventId)?.nodeDefinitionId

  const candidates = data.sequences
    .filter((sequence) => boundariesById.has(sequence.boundaryEventId))
    .map((sequence) => ({
      taskId: boundaryOwner(sequence),
      node: nodesById.get(sequence.endNodeId),
      boundaryEvent: boundariesById.get(sequence.boundaryEventId),
    }))
    .filter(({ taskId, node }) => !!node && node.id !== taskId)
    .filter(({ node }) => !START_TYPES.includes(node.type))
    .filter(({ taskId, node }) =>
      data.sequences
        .filter((sequence) => sequence.endNodeId === node.id)
        .every((sequence) => boundaryOwner(sequence) === taskId)
    )
  const taskIds = new Set(candidates.map(({ taskId }) => taskId))
  const children = candidates
    .filter(({ node }) => !taskIds.has(node.id))
    // a child reached from two of the task's events is placed once
    .filter((child, index, all) => all.findIndex(({ node }) => node.id === child.node.id) === index)

  return new Map(
    [...new Set(children.map(({ taskId }) => taskId))].map((taskId) => [
      taskId,
      children
        .filter((child) => child.taskId === taskId)
        .sort(
          (a, b) =>
            (a.boundaryEvent.layoutOptions?.x ?? 0) - (b.boundaryEvent.layoutOptions?.x ?? 0) ||
            a.node.id - b.node.id
        )
        .map(({ node, boundaryEvent }) => ({ node, boundaryEvent })),
    ])
  )
}

const startIdsOf = (data: CanvasData): number[] =>
  data.nodes.filter((node) => START_TYPES.includes(node.type)).map((node) => node.id)

/** The edge of its task a boundary event sits on, as the direction that edge faces. */
const edgeOf = (
  boundaryEvent: BoundaryEventRender,
  task: NodeDefinitionRender
): ExceptionDirection =>
  (boundaryEvent.layoutOptions?.y ?? 0) + getNodeDimensions(boundaryEvent.type).height / 2 <
  getNodeDimensions(task.type).height / 2
    ? 'up'
    : 'down'

type ChildPlacement = { nodeId: number; x: number; width: number; height: number }

/** Children in a row, each centred on its event, pushed right past its left neighbour if needed. */
const childRow = (
  children: { node: NodeDefinitionRender; boundaryEvent: BoundaryEventRender }[]
): ChildPlacement[] =>
  children.reduce<ChildPlacement[]>((placed, { node, boundaryEvent }) => {
    const size = getNodeDimensions(node.type)
    const eventCentre =
      (boundaryEvent.layoutOptions?.x ?? 0) + getNodeDimensions(boundaryEvent.type).width / 2
    const previous = placed[placed.length - 1]
    const x = Math.max(
      eventCentre - size.width / 2,
      previous ? previous.x + previous.width + EXCEPTION_CHILD_SPACING : -Infinity
    )

    return [...placed, { nodeId: node.id, x, ...size }]
  }, [])

/** Branch ranks with each exception flow counted towards the side its block sends it. */
export const directedRanks = (data: CanvasData, blocks: ExceptionBlock[]): Map<number, number> => {
  const eventDirections = new Map(blocks.flatMap(({ eventDirections }) => [...eventDirections]))

  return branchRanks(data, startIdsOf(data), (sequence) =>
    eventDirections.get(sequence.boundaryEventId) === 'up' ? -1 : 1
  )
}

export function exceptionBlocks(
  data: CanvasData,
  ranks: ReadonlyMap<number, number> = branchRanks(data, startIdsOf(data))
): ExceptionBlock[] {
  const nodesById = new Map(data.nodes.map((node) => [node.id, node]))
  const laneIndex = new Map(orderLanes(data.swimlanes).map((lane, index) => [lane.id, index]))

  return [...exceptionChildrenByTask(data).entries()].map(([taskId, allChildren]) => {
    const task = nodesById.get(taskId)
    const taskSize = getNodeDimensions(task.type)
    const rank = ranks.get(taskId) ?? 0
    const directionOf = (child: NodeDefinitionRender, boundaryEvent: BoundaryEventRender) => {
      const laneStep =
        (laneIndex.get(child.swimlaneDefinitionId) ?? 0) -
        (laneIndex.get(task.swimlaneDefinitionId) ?? 0)
      if (laneStep) {
        return laneStep > 0 ? 'down' : 'up'
      }
      if (rank) {
        return rank > 0 ? 'down' : 'up'
      }

      return edgeOf(boundaryEvent, task)
    }
    const directed = allChildren.map((child) => ({
      ...child,
      direction: directionOf(child.node, child.boundaryEvent) as ExceptionDirection,
      edge: edgeOf(child.boundaryEvent, task),
    }))
    const groups = EXCEPTION_DIRECTIONS.map((direction) => {
      const facing = directed.filter(
        (child) => child.direction === direction && child.edge === direction
      )
      const wrapping = directed
        .filter((child) => child.direction === direction && child.edge !== direction)
        .reverse()
      const besideEventId = facing[0]?.boundaryEvent.id
      const facingSteps = facing.filter(({ boundaryEvent }) => boundaryEvent.id !== besideEventId)

      return {
        direction,
        row: childRow(facing.filter(({ boundaryEvent }) => boundaryEvent.id === besideEventId)),
        steps: [
          ...facingSteps.map(({ node }, index) => ({
            nodeId: node.id,
            wraps: false,
            // earlier steps run further out, beyond the later ones and every wrapping return
            level: facingSteps.length - index + wrapping.length,
          })),
          ...wrapping.map(({ node }, index) => ({
            nodeId: node.id,
            wraps: true,
            level: index + 1,
            returnLevel: wrapping.length - index,
          })),
        ],
      }
    })
    const [below, above] = groups
    const rowHeight = (row: ChildPlacement[]): number =>
      Math.max(0, ...row.map(({ height }) => height))
    const aboveY = -EXCEPTION_CHILD_GAP - rowHeight(above.row)
    const belowY = taskSize.height + EXCEPTION_CHILD_GAP
    const rows = [
      ...above.row.map((child) => ({ ...child, y: aboveY })),
      ...below.row.map((child) => ({ ...child, y: belowY })),
    ]
    const left = Math.min(0, ...rows.map(({ x }) => x))
    const right = Math.max(taskSize.width, ...rows.map(({ x, width }) => x + width))
    const top = above.row.length ? aboveY : 0
    const bottom = below.row.length ? belowY + rowHeight(below.row) : taskSize.height

    return {
      taskId,
      taskOffset: { x: -left, y: -top },
      children: rows.map(({ nodeId, x, y }) => ({ nodeId, offset: { x: x - left, y: y - top } })),
      staircases: { down: below.steps, up: above.steps },
      eventDirections: new Map(
        directed.map(({ boundaryEvent, direction }) => [boundaryEvent.id, direction] as const)
      ),
      width: right - left,
      height: bottom - top,
    }
  })
}

/** How many times directions are re-derived from the ranks they produce before settling. */
const MAX_DIRECTION_ROUNDS = 3

const directionsOf = (blocks: ExceptionBlock[]): string =>
  JSON.stringify(blocks.map(({ taskId, eventDirections }) => [taskId, [...eventDirections]]))

/**
 * Exception blocks whose directions agree with the ranks the layout places them by. A block's
 * directions come from its task's rank, but an exception flow sent up moves everything after it
 * above the main row — including other tasks whose own flows must then go up as well — so the
 * two are re-derived from each other until they agree.
 */
export function settledExceptionBlocks(data: CanvasData): {
  blocks: ExceptionBlock[]
  ranks: Map<number, number>
} {
  const settle = (blocks: ExceptionBlock[], rounds: number) => {
    const ranks = directedRanks(data, blocks)
    const next = exceptionBlocks(data, ranks)

    return rounds > 0 && directionsOf(next) !== directionsOf(blocks)
      ? settle(next, rounds - 1)
      : { blocks: next, ranks }
  }

  return settle(exceptionBlocks(data), MAX_DIRECTION_ROUNDS)
}

/** Splits every laid-out block back into its task and children, keyed like plain nodes. */
export function expandExceptionBlocks(
  positions: ReadonlyMap<string, LayoutPoint>,
  blocks: ExceptionBlock[]
): Map<string, LayoutPoint> {
  const blockKeys = new Set(blocks.map(({ taskId }) => blockKey(taskId)))
  const at = (origin: LayoutPoint, offset: Point): LayoutPoint => ({
    x: origin.x + offset.x,
    y: origin.y + offset.y,
  })

  return new Map([
    ...[...positions.entries()].filter(([id]) => !blockKeys.has(id)),
    ...blocks
      .filter(({ taskId }) => positions.has(blockKey(taskId)))
      .flatMap((block) => {
        const origin = positions.get(blockKey(block.taskId))

        return [
          [nodeKey(block.taskId), at(origin, block.taskOffset)] as const,
          ...block.children.map(
            ({ nodeId, offset }) => [nodeKey(nodeId), at(origin, offset)] as const
          ),
        ]
      }),
  ])
}

/**
 * One flat ELK graph for the whole process, lanes ignored, so columns line up across lanes.
 * Each node is seeded in its lane's band (lane order × a large stride), at its branch rank within
 * it, so the semi-interactive crossing minimization keeps lanes and gateway branches in order
 * inside every column. The main path is weighted to run straight, gateways give every branch its
 * own port in stacking order, and staircase steps are chained a column and a row apart.
 *
 * Each exception block is a single node with fixed ports where its members' connections really
 * attach: the task's inputs on the west side at the task's height, every member's outputs on the
 * east side at that member's height, and boundary events on the side their flows leave from
 * (south for down, north for up) at their events. Flows between members of one block (a task's
 * boundary flow into its own child) stay out of ELK.
 */
export function toProcessElkGraph(
  data: CanvasData,
  blocks: ExceptionBlock[] = exceptionBlocks(data),
  ranks: ReadonlyMap<number, number> = directedRanks(data, blocks)
): ElkNode {
  const laneIndex = new Map(orderLanes(data.swimlanes).map((lane, index) => [lane.id, index]))
  const boundaryEvents = drawnBoundaryEvents(data)
  const boundariesById = new Map(
    boundaryEvents.map((boundaryEvent) => [boundaryEvent.id, boundaryEvent])
  )
  const nodesById = new Map(data.nodes.map((node) => [node.id, node]))
  const blockByMember = new Map(
    blocks.flatMap((block) => [
      [block.taskId, block] as const,
      ...block.children.map(({ nodeId }) => [nodeId, block] as const),
    ])
  )
  const memberOffset = (block: ExceptionBlock, nodeId: number): Point =>
    nodeId === block.taskId
      ? block.taskOffset
      : block.children.find((child) => child.nodeId === nodeId).offset
  const startIds = startIdsOf(data)
  const laneHeight = new Map(
    data.swimlanes.map((swimlane) => [swimlane.id, swimlane.layoutOptions?.height ?? 0])
  )
  // A node no start event reaches has no rank: it keeps its drawn place relative to the lane's
  // middle, among the ranked nodes rather than above all of them
  const rankSeed = (node: NodeDefinitionRender): Point => ({
    x: node.layoutOptions?.x ?? 0,
    y:
      (laneIndex.get(node.swimlaneDefinitionId) ?? 0) * LANE_ORDER_STRIDE +
      RANK_SEED_CENTRE +
      (ranks.has(node.id)
        ? ranks.get(node.id) * RANK_SEED_STEP
        : (node.layoutOptions?.y ?? 0) - (laneHeight.get(node.swimlaneDefinitionId) ?? 0) / 2),
  })
  // A staircase step is entered at its corner nearest the task and leads to the next step from
  // its far side, so ELK lines each step up with the far side of the step before it: a row further
  // out. Each step is seeded just outside its task's row, before the next branch row starts, so a
  // staircase stays together instead of mixing with a neighbouring branch.
  const staircaseSteps = blocks.flatMap((block) =>
    EXCEPTION_DIRECTIONS.flatMap((direction) =>
      block.staircases[direction].map((step, index, steps) => ({
        ...step,
        direction,
        seedY:
          rankSeed(nodesById.get(block.taskId)).y +
          (direction === 'down' ? 1 : -1) * (index + 1) * STAIRCASE_SEED_STEP,
        previous: index ? steps[index - 1].nodeId : undefined,
      }))
    )
  )
  const stepsById = new Map(staircaseSteps.map((step) => [step.nodeId, step]))
  const stepInKey = (nodeId: number): string => `${nodeKey(nodeId)}::step-in`
  const stepOutKey = (nodeId: number): string => `${nodeKey(nodeId)}::step-out`
  const seed = (node: NodeDefinitionRender): Point =>
    stepsById.has(node.id)
      ? { x: node.layoutOptions?.x ?? 0, y: stepsById.get(node.id).seedY }
      : rankSeed(node)
  // The semi-interactive crossing minimization reads the seed from `elk.position`
  const seedPosition = (node: NodeDefinitionRender): LayoutOptions => {
    const { x, y } = seed(node)

    return { 'elk.position': `(${x}, ${y})` }
  }
  const startConstraint = (node: NodeDefinitionRender): LayoutOptions =>
    START_TYPES.includes(node.type) ? { 'elk.layered.layering.layerConstraint': 'FIRST' } : {}
  const port = (id: string, side: string, position?: Point) => ({
    id,
    width: 1,
    height: 1,
    ...position,
    layoutOptions: { 'elk.port.side': side },
  })
  const blockInPort = (block: ExceptionBlock): string => `${blockKey(block.taskId)}::in`
  const blockOutPort = (block: ExceptionBlock, nodeId: number): string =>
    `${blockKey(block.taskId)}::out-${nodeId}`
  const blockBoundaryPort = (block: ExceptionBlock, boundaryEventId: number): string =>
    `${blockKey(block.taskId)}::boundary-${boundaryEventId}`

  const boundaryPortX = (block: ExceptionBlock, boundaryEvent: BoundaryEventRender): number =>
    memberOffset(block, boundaryEvent.nodeDefinitionId).x +
    (boundaryEvent.layoutOptions?.x ?? 0) +
    getNodeDimensions(boundaryEvent.type).width / 2

  // Gateways give each branch its own east port, in the order the branches should be stacked — on
  // the gateway, or on the block of the task it is an exception child of
  const branchPorts = new Map(
    data.nodes.flatMap((node) =>
      gatewayBranchOffsets(node, data).map(
        ({ sequence, offset }) => [sequence.id, { nodeId: node.id, offset }] as const
      )
    )
  )
  const branchPortKey = (sequenceId: number): string => {
    const { nodeId } = branchPorts.get(sequenceId)
    const block = blockByMember.get(nodeId)

    return `${block ? blockKey(block.taskId) : nodeKey(nodeId)}::branch-${sequenceId}`
  }
  const branchPortsOf = (nodeId: number, x: number, centreY: number) =>
    [...branchPorts.entries()]
      .filter(([, branch]) => branch.nodeId === nodeId)
      .map(([sequenceId, { offset }]) =>
        port(branchPortKey(sequenceId), 'EAST', { x, y: centreY + offset * GATEWAY_PORT_STEP })
      )

  const blockNodes = blocks.map((block) => {
    const task = nodesById.get(block.taskId)
    const members = [block.taskId, ...block.children.map(({ nodeId }) => nodeId)]

    return {
      id: blockKey(block.taskId),
      width: block.width,
      height: block.height,
      ...seed(task),
      layoutOptions: {
        ...startConstraint(task),
        ...seedPosition(task),
        'elk.portConstraints': 'FIXED_POS',
      },
      ports: [
        port(blockInPort(block), 'WEST', {
          x: -1,
          y: block.taskOffset.y + getNodeDimensions(task.type).height / 2,
        }),
        ...members.map((nodeId) =>
          port(blockOutPort(block, nodeId), 'EAST', {
            x: block.width,
            y:
              memberOffset(block, nodeId).y +
              getNodeDimensions(nodesById.get(nodeId).type).height / 2,
          })
        ),
        ...members.flatMap((nodeId) =>
          branchPortsOf(
            nodeId,
            block.width,
            memberOffset(block, nodeId).y + getNodeDimensions(nodesById.get(nodeId).type).height / 2
          )
        ),
        ...boundaryEvents
          .filter((boundaryEvent) => members.includes(boundaryEvent.nodeDefinitionId))
          .map((boundaryEvent) =>
            block.eventDirections.get(boundaryEvent.id) === 'up'
              ? port(blockBoundaryPort(block, boundaryEvent.id), 'NORTH', {
                  x: boundaryPortX(block, boundaryEvent),
                  y: 0,
                })
              : port(blockBoundaryPort(block, boundaryEvent.id), 'SOUTH', {
                  x: boundaryPortX(block, boundaryEvent),
                  y: block.height,
                })
          ),
      ],
    }
  })

  // Sequences attach at the middle of a node's side, so every node gets its input and output
  // ports at its vertical centre: ELK then straightens a chain centre to centre instead of
  // attaching edges anywhere along the side (which left small steps between nodes of different
  // heights, e.g. an event next to a task).
  const main = mainPath(data, startIds)

  const plainNodes = data.nodes
    .filter((node) => !blockByMember.has(node.id))
    .map((node) => {
      const size = getNodeDimensions(node.type)

      return {
        id: nodeKey(node.id),
        ...size,
        ...seed(node),
        layoutOptions: {
          ...startConstraint(node),
          ...seedPosition(node),
          'elk.portConstraints': 'FIXED_POS',
        },
        ports: [
          port(inPortKey(node.id), 'WEST', { x: -1, y: size.height / 2 }),
          port(outPortKey(node.id), 'EAST', { x: size.width, y: size.height / 2 }),
          ...(stepsById.has(node.id)
            ? stepsById.get(node.id).direction === 'down'
              ? [
                  port(stepInKey(node.id), 'WEST', { x: -1, y: 0 }),
                  port(stepOutKey(node.id), 'SOUTH', { x: size.width / 2, y: size.height }),
                ]
              : [
                  port(stepInKey(node.id), 'WEST', { x: -1, y: size.height }),
                  port(stepOutKey(node.id), 'NORTH', { x: size.width / 2, y: 0 }),
                ]
            : []),
          ...branchPortsOf(node.id, size.width, size.height / 2),
          ...boundaryEvents
            .filter((boundaryEvent) => boundaryEvent.nodeDefinitionId === node.id)
            .map((boundaryEvent) =>
              port(portKey(boundaryEvent.id), 'SOUTH', {
                x:
                  (boundaryEvent.layoutOptions?.x ?? 0) +
                  getNodeDimensions(boundaryEvent.type).width / 2,
                y: size.height,
              })
            ),
        ],
      }
    })

  const sourceOwner = (sequence: CanvasData['sequences'][number]): number =>
    sequence.boundaryEventId
      ? boundariesById.get(sequence.boundaryEventId).nodeDefinitionId
      : sequence.startNodeId
  const elementOf = (nodeId: number): string =>
    blockByMember.has(nodeId) ? blockKey(blockByMember.get(nodeId).taskId) : nodeKey(nodeId)
  const sourceOf = (sequence: CanvasData['sequences'][number]): string => {
    const block = blockByMember.get(sourceOwner(sequence))
    if (sequence.boundaryEventId) {
      return block
        ? blockBoundaryPort(block, sequence.boundaryEventId)
        : portKey(sequence.boundaryEventId)
    }

    if (branchPorts.has(sequence.id)) {
      return branchPortKey(sequence.id)
    }

    return block ? blockOutPort(block, sequence.startNodeId) : outPortKey(sequence.startNodeId)
  }
  const targetOf = (sequence: CanvasData['sequences'][number]): string => {
    const block = blockByMember.get(sequence.endNodeId)

    if (!block) {
      return sequence.boundaryEventId && stepsById.has(sequence.endNodeId)
        ? stepInKey(sequence.endNodeId)
        : inPortKey(sequence.endNodeId)
    }

    return block.taskId === sequence.endNodeId ? blockInPort(block) : elementOf(sequence.endNodeId)
  }

  return {
    id: 'process',
    children: [...plainNodes, ...blockNodes],
    edges: data.sequences
      .filter((sequence) => nodesById.has(sequence.endNodeId))
      .filter((sequence) =>
        sequence.boundaryEventId
          ? boundariesById.has(sequence.boundaryEventId)
          : nodesById.has(sequence.startNodeId)
      )
      // self-loops, and flows inside one block (a task's boundary flow into its own child)
      .filter((sequence) => elementOf(sourceOwner(sequence)) !== elementOf(sequence.endNodeId))
      .map((sequence): ElkExtendedEdge => ({
        id: `sequence-${sequence.id}`,
        sources: [sourceOf(sequence)],
        targets: [targetOf(sequence)],
        ...((main.sequenceIds.has(sequence.id) ||
          (sequence.boundaryEventId && stepsById.has(sequence.endNodeId))) && {
          layoutOptions: { 'elk.layered.priority.straightness': MAIN_PATH_STRAIGHTNESS },
        }),
      }))
      .concat(
        // Invisible links that keep each parallel split and its join on one line; never routed
        main.parallelPairs
          .filter(
            ({ splitId, joinId }) => !blockByMember.has(splitId) && !blockByMember.has(joinId)
          )
          .map(({ splitId, joinId }) => ({
            id: `parallel-pair-${splitId}-${joinId}`,
            sources: [outPortKey(splitId)],
            targets: [inPortKey(joinId)],
            layoutOptions: { 'elk.layered.priority.straightness': MAIN_PATH_STRAIGHTNESS },
          }))
      )
      .concat(
        // Invisible links from each staircase step to the next: a column right, a row lower
        staircaseSteps
          .filter(({ previous }) => previous !== undefined)
          .map(({ nodeId, previous }) => ({
            id: `staircase-${previous}-${nodeId}`,
            sources: [stepOutKey(previous)],
            targets: [stepInKey(nodeId)],
            layoutOptions: { 'elk.layered.priority.straightness': MAIN_PATH_STRAIGHTNESS },
          }))
      ),
  }
}

/**
 * Packs ELK's flat drawing into lanes: every node keeps its ELK column (x, shared by all lanes)
 * and its lane. Lanes keep their size and only grow when their arranged content needs more room:
 * each lane keeps its height and every lane keeps the widest lane's width. A lane's nodes move
 * together, centred vertically in the lane, and lanes are restacked in their current order from
 * the first lane's current y (a grown lane pushes the ones below it down). Uniform shifts inside
 * a lane cannot create overlaps ELK did not have.
 */
export function packIntoLanes(
  data: CanvasData,
  positions: ReadonlyMap<string, LayoutPoint>
): Pick<ProcessLayoutPlan, 'nodes' | 'swimlanes'> {
  const lanes = orderLanes(data.swimlanes)
  const placed = data.nodes.filter((node) => positions.has(nodeKey(node.id)))
  const minX = Math.min(...placed.map((node) => positions.get(nodeKey(node.id)).x))
  const laneSize = (lane: SwimlaneSummary): LaneLayout => ({
    ...SWIMLANE_DEFAULT_LAYOUT_OPTIONS,
    ...lane.layoutOptions,
  })

  const laneContents = lanes.map((lane) => {
    const members = placed.filter((node) => node.swimlaneDefinitionId === lane.id)
    const minY = Math.min(...members.map((node) => positions.get(nodeKey(node.id)).y))
    const packed = members.map((node) => {
      const { x, y } = positions.get(nodeKey(node.id))
      const { width, height } = getNodeDimensions(node.type)

      return {
        node,
        layoutOptions: {
          x: snapByCentre(x - minX + LANE_CONTENT_LEFT, width),
          y: snapByCentre(y - minY, height),
        },
      }
    })
    const contentHeight = Math.max(
      0,
      ...packed.map(
        ({ node, layoutOptions }) => layoutOptions.y + getNodeDimensions(node.type).height
      )
    )
    const right = Math.max(
      0,
      ...packed.map(
        ({ node, layoutOptions }) => layoutOptions.x + getNodeDimensions(node.type).width
      )
    )
    const height = Math.max(
      MIN_LANE_HEIGHT,
      laneSize(lane).height,
      snap(LANE_CONTENT_TOP + contentHeight + LANE_CONTENT_BOTTOM)
    )
    const top = snap((height - contentHeight) / 2)

    return {
      lane,
      height,
      right,
      nodes: packed.map(({ node, layoutOptions }) => ({
        node,
        layoutOptions: { x: layoutOptions.x, y: layoutOptions.y + top },
      })),
    }
  })

  const width = Math.max(
    MIN_LANE_WIDTH,
    ...lanes.map((lane) => laneSize(lane).width),
    ...laneContents.map(({ right }) => snap(right + LANE_CONTENT_RIGHT))
  )
  const top = lanes.length ? laneY(lanes[0]) : 0
  const laneTops = laneContents.map(
    (_, index) => top + laneContents.slice(0, index).reduce((sum, { height }) => sum + height, 0)
  )

  return {
    nodes: laneContents.flatMap(({ nodes }) => nodes),
    swimlanes: laneContents.map(({ lane, height }, index) => ({
      swimlane: lane,
      layoutOptions: { x: lane.layoutOptions?.x ?? 0, y: laneTops[index], width, height },
    })),
  }
}

/**
 * Re-applies each exception child's exact horizontal offset from its task after lane packing,
 * which snaps every x to the grid on its own. A child centred on its event is never on the
 * grid (an event's centre sits at x + 16), and snapping child and task separately would bend its
 * drop by a few pixels. Only x moves — rows stay snapped — and lanes share their x origin, so this
 * also holds across lanes.
 */
export function alignExceptionChildren(
  data: CanvasData,
  packed: Pick<ProcessLayoutPlan, 'nodes' | 'swimlanes'>,
  blocks: ExceptionBlock[]
): Pick<ProcessLayoutPlan, 'nodes' | 'swimlanes'> {
  const laneX = new Map(
    packed.swimlanes.map(({ swimlane, layoutOptions }) => [swimlane.id, layoutOptions.x])
  )
  const laneOf = new Map(data.nodes.map((node) => [node.id, node.swimlaneDefinitionId]))
  const positions = new Map(packed.nodes.map(({ node, layoutOptions }) => [node.id, layoutOptions]))
  const absoluteX = (nodeId: number): number =>
    laneX.get(laneOf.get(nodeId)) + positions.get(nodeId).x
  const alignedX = new Map(
    blocks
      .filter(({ taskId }) => positions.has(taskId))
      .flatMap((block) =>
        block.children
          .filter(({ nodeId }) => positions.has(nodeId))
          .map(({ nodeId, offset }) => [
            nodeId,
            absoluteX(block.taskId) + offset.x - block.taskOffset.x - laneX.get(laneOf.get(nodeId)),
          ])
      )
  )

  return {
    ...packed,
    nodes: packed.nodes.map((entry) =>
      alignedX.has(entry.node.id)
        ? { ...entry, layoutOptions: { ...entry.layoutOptions, x: alignedX.get(entry.node.id) } }
        : entry
    ),
  }
}

/** Fresh routes for every drawn sequence, between the nodes' rects in `data`. */
/** Every node's rect on the canvas: its lane-relative position plus its lane's. */
function absoluteNodeRects(data: CanvasData): Map<number, Rect> {
  const lanes = new Map(data.swimlanes.map((swimlane) => [swimlane.id, swimlane]))

  return new Map(
    data.nodes.map((node) => {
      const lane = lanes.get(node.swimlaneDefinitionId)

      return [
        node.id,
        {
          x: (lane?.layoutOptions?.x ?? 0) + (node.layoutOptions?.x ?? 0),
          y: (lane?.layoutOptions?.y ?? 0) + (node.layoutOptions?.y ?? 0),
          ...getNodeDimensions(node.type),
        },
      ] as const
    })
  )
}

export function routeAllSequences(
  data: CanvasData,
  blocks: ExceptionBlock[] = exceptionBlocks(data)
): ProcessLayoutPlan['sequences'] {
  const nodeRects = absoluteNodeRects(data)

  const typeOf = new Map(data.nodes.map((node) => [node.id, node.type]))
  const ownerOfBoundary = new Map(
    data.boundaryEvents.map((boundaryEvent) => [boundaryEvent.id, boundaryEvent.nodeDefinitionId])
  )
  const staircaseStepOf = new Map(
    blocks.flatMap(({ staircases }) =>
      EXCEPTION_DIRECTIONS.flatMap((direction) =>
        staircases[direction].map((step) => [step.nodeId, { ...step, direction }] as const)
      )
    )
  )
  const edges = toFlowGraph(data, { readonly: true })
    .edges.filter((edge) => edge.type === SEQUENCE_EDGE_TYPE)
    .map((edge) => edge.data as SequenceEdgeData)
  const loopSpan = ({ sequence, sourceRect, targetRect }: SequenceEdgeData): number =>
    !sequence.boundaryEventId && centreOf(targetRect).x < centreOf(sourceRect).x
      ? centreOf(sourceRect).x - centreOf(targetRect).x
      : 0
  const route = (
    { sequence, sourceRect, targetRect }: SequenceEdgeData,
    loopChannels: Rect[],
    routed: Point[][]
  ) => {
    // A boundary sequence leaves from its event, so its owning task (the sequence's startNodeId)
    // stays an obstacle: a route may leave along the task's border but not cut through it
    const obstacles = [...nodeRects.entries()]
      .filter(
        ([id]) =>
          (!!sequence.boundaryEventId || id !== sequence.startNodeId) && id !== sequence.endNodeId
      )
      .map(([, rect]) => rect)
    const preferred = preferredRoute(
      sequence,
      typeOf.get(sequence.startNodeId),
      sourceRect,
      targetRect,
      obstacles,
      loopChannels,
      sequence.boundaryEventId && staircaseStepOf.has(sequence.endNodeId)
        ? {
            ...staircaseStepOf.get(sequence.endNodeId),
            task: nodeRects.get(ownerOfBoundary.get(sequence.boundaryEventId)),
          }
        : undefined
    )
    const points = routeAvoidingNodes(sourceRect, targetRect, obstacles, preferred, routed)

    return { points, preferredTaken: !!preferred && points === preferred }
  }
  // Shorter loops are routed first, so they nest inside longer ones; each loop's channel is an
  // obstacle for the loops after it, which therefore run below it instead of on top of it. Every
  // route is then drawn for the ones after it, which avoid crossing or running along it.
  const { loopRoutes } = edges
    .filter((edge) => loopSpan(edge) > 0)
    .sort((a, b) => loopSpan(a) - loopSpan(b))
    .reduce<{ loopRoutes: [number, Point[]][]; channels: Rect[] }>(
      ({ loopRoutes: routed, channels }, edge) => {
        const { points, preferredTaken } = route(
          edge,
          channels,
          routed.map(([, loopPoints]) => loopPoints)
        )

        return {
          loopRoutes: [...routed, [edge.sequence.id, points]],
          channels: preferredTaken ? [...channels, ...loopChannelOf(points)] : channels,
        }
      },
      { loopRoutes: [], channels: [] }
    )
  const routedLoops = new Map(loopRoutes)

  return edges.reduce<{ routes: ProcessLayoutPlan['sequences']; routed: Point[][] }>(
    ({ routes, routed }, edge) => {
      const points = routedLoops.get(edge.sequence.id) ?? route(edge, [], routed).points

      return {
        routes: [...routes, { sequence: edge.sequence, points }],
        routed: routedLoops.has(edge.sequence.id) ? routed : [...routed, points],
      }
    },
    { routes: [], routed: [...routedLoops.values()] }
  ).routes
}

const GATEWAY_TYPES: string[] = [GatewayNodes.GATEWAY_EXCLUSIVE, GatewayNodes.GATEWAY_PARALLEL]
/** Clearance between a return loop's channel and the lowest node it passes under. */
const LOOP_CLEARANCE = 30

const centreOf = (rect: Rect): Point => ({
  x: rect.x + rect.width / 2,
  y: rect.y + rect.height / 2,
})

/**
 * The route a sequence should take when it is clear of other nodes:
 * - an exception flow into a staircase step runs out along its own level, then into the step;
 * - an exception flow into a node below its event drops down into it, above it rises into it;
 * - a loop back to an earlier node runs below everything between the two;
 * - a gateway branch leaves BPMN-style: to a node below from the bottom, above from the top,
 *   level from the right, entering the target from its left.
 * Anything else takes the canvas's own route.
 */
function preferredRoute(
  sequence: SequencePoints,
  sourceType: string | undefined,
  sourceRect: Rect,
  targetRect: Rect,
  obstacles: Rect[],
  loopChannels: Rect[],
  staircaseStep?: StaircaseRouteStep
): Point[] | undefined {
  if (staircaseStep) {
    return staircaseDrop(sourceRect, targetRect, staircaseStep.task, staircaseStep)
  }
  if (sequence.boundaryEventId) {
    if (targetRect.y >= sourceRect.y + sourceRect.height) {
      return downwardDrop(sourceRect, targetRect)
    }

    return targetRect.y + targetRect.height <= sourceRect.y
      ? upwardRise(sourceRect, targetRect)
      : undefined
  }
  if (centreOf(targetRect).x < centreOf(sourceRect).x) {
    return loopBelow(sourceRect, targetRect, [...obstacles, ...loopChannels])
  }
  if (GATEWAY_TYPES.includes(sourceType) && targetRect.x >= sourceRect.x + sourceRect.width) {
    const targetCentreY = centreOf(targetRect).y
    const side =
      targetCentreY > sourceRect.y + sourceRect.height
        ? Position.Bottom
        : targetCentreY < sourceRect.y
          ? Position.Top
          : Position.Right

    return routeBetweenRectPositions(sourceRect, side, targetRect, Position.Left)
  }

  return undefined
}

/** The horizontal channel of a loop drawn by `loopBelow`, as a flat obstacle. */
const loopChannelOf = (points: Point[]): Rect[] =>
  points.length === 4
    ? [
        {
          x: Math.min(points[1].x, points[2].x),
          y: points[1].y,
          width: Math.abs(points[1].x - points[2].x),
          height: 0,
        },
      ]
    : []

/**
 * A return loop from `source` back to `target` (left of it): down from the source's bottom, left
 * along a channel below every node between the two, and up into the target's bottom.
 */
export function loopBelow(source: Rect, target: Rect, obstacles: Rect[]): Point[] {
  const start = { x: source.x + source.width / 2, y: source.y + source.height }
  const end = { x: target.x + target.width / 2, y: target.y + target.height }
  const [left, right] = [Math.min(start.x, end.x), Math.max(start.x, end.x)]
  const lowest = Math.max(
    start.y,
    end.y,
    ...obstacles
      .filter((rect) => rect.x < right && rect.x + rect.width > left)
      .map((rect) => rect.y + rect.height)
  )
  const channel = snap(lowest + LOOP_CLEARANCE)

  return [start, { x: start.x, y: channel }, { x: end.x, y: channel }, end]
}

type StaircaseRouteStep = StaircaseStep & { direction: ExceptionDirection; task: Rect }

/** Vertical distance between the levels staircase flows run along beside their task. */
const STAIRCASE_LEVEL_STEP = 10

/**
 * An exception flow into a staircase step: out of the event to its own level beside the task,
 * right to the gap before the step's column, then up or down to the step's middle and into its
 * left side. A flow whose event faces the main row leaves towards it and wraps round the task's
 * right side first, coming back past the task's edge on its own return level. The levels are
 * chosen so no two of a task's staircase flows cross.
 */
export function staircaseDrop(
  source: Rect,
  target: Rect,
  task: Rect,
  { direction, wraps, level: levelIndex, returnLevel = 1 }: StaircaseRouteStep
): Point[] {
  const outward = direction === 'down' ? 1 : -1
  const turnX = snap(target.x - STAIRCASE_TURN_GAP)
  const end = { x: target.x, y: target.y + target.height / 2 }
  if (!wraps) {
    const start = {
      x: source.x + source.width / 2,
      y: outward > 0 ? source.y + source.height : source.y,
    }
    const level = snap(start.y) + outward * STAIRCASE_LEVEL_STEP * levelIndex

    return [start, { x: start.x, y: level }, { x: turnX, y: level }, { x: turnX, y: end.y }, end]
  }

  const start = {
    x: source.x + source.width / 2,
    y: outward > 0 ? source.y : source.y + source.height,
  }
  const farLevel = snap(start.y) - outward * STAIRCASE_LEVEL_STEP * levelIndex
  const aroundX = snap(task.x + task.width) + STAIRCASE_LEVEL_STEP * levelIndex
  const taskEdge = outward > 0 ? task.y + task.height : task.y
  const returnY = snap(taskEdge) + outward * STAIRCASE_LEVEL_STEP * returnLevel

  return [
    start,
    { x: start.x, y: farLevel },
    { x: aroundX, y: farLevel },
    { x: aroundX, y: returnY },
    { x: turnX, y: returnY },
    { x: turnX, y: end.y },
    end,
  ]
}

/** An exception flow up from its event into the bottom of a child above it; `downwardDrop` mirrored. */
export function upwardRise(source: Rect, target: Rect): Point[] {
  const start = { x: source.x + source.width / 2, y: source.y }
  const end = { x: target.x + target.width / 2, y: target.y + target.height }
  if (start.x === end.x) {
    return [start, end]
  }
  const middle = (start.y + end.y) / 2

  return [start, { x: start.x, y: middle }, { x: end.x, y: middle }, end]
}

/**
 * From the bottom centre of `source` to the top centre of `target` below it: one straight drop when
 * the centres line up, otherwise down to halfway, across, and down — three segments, no stubs.
 */
export function downwardDrop(source: Rect, target: Rect): Point[] {
  const start = { x: source.x + source.width / 2, y: source.y + source.height }
  const end = { x: target.x + target.width / 2, y: target.y }
  if (start.x === end.x) {
    return [start, end]
  }
  const middle = (start.y + end.y) / 2

  return [start, { x: start.x, y: middle }, { x: end.x, y: middle }, end]
}

const ROUTE_SIDES = [Position.Right, Position.Bottom, Position.Top, Position.Left]
/** Whether the axis-aligned segment a→b passes through rect's interior (touching the border is fine). */
const segmentCrossesRect = (a: Point, b: Point, rect: Rect): boolean =>
  Math.min(a.x, b.x) < rect.x + rect.width - 1 &&
  Math.max(a.x, b.x) > rect.x + 1 &&
  Math.min(a.y, b.y) < rect.y + rect.height - 1 &&
  Math.max(a.y, b.y) > rect.y + 1

export const routeCrossesAny = (points: Point[], obstacles: Rect[]): boolean =>
  points
    .slice(1)
    .some((point, index) =>
      obstacles.some((rect) => segmentCrossesRect(points[index], point, rect))
    )

/** `rect` grown by the clearance a detour keeps from it, so it never runs along a node's border. */
const toClearedBox = ({ x, y, width, height }: Rect): RouteBox =>
  padBox(toRouteBox(x, y, width, height), NODE_CLEARANCE)

/**
 * How far past the box spanning a detour's two ends it looks for nodes to go around: about two
 * columns of BPMN nodes either way. The search grows with the cube of the nodes it sees, so the
 * shared router's margin (sized for workflow cards) would take in most of a large process.
 */
const DETOUR_MARGIN = 480
/**
 * How many grid points a detour search may visit before giving up. A route that exists turns up
 * within a few hundred; a boxed-in end would otherwise visit the whole grid.
 */
const DETOUR_VISITS = 2_000

/** How far a detour runs straight out of its node before it may turn (past its clearance). */
const DETOUR_STUB = 28

/**
 * A winding route between the two ends of `canvasRoute`, leaving and entering on the same sides,
 * through the gaps between `obstacles` (an A* search over their borders). Null when the ends are
 * boxed in, or the detour would still cross a node.
 */
function detourAround(
  canvasRoute: Point[],
  sourceRect: Rect,
  targetRect: Rect,
  obstacles: Rect[]
): Point[] | null {
  if (canvasRoute.length < 2) {
    return null
  }
  const outward = (from: Point, towards: Point): Point => ({
    x: from.x + Math.sign(towards.x - from.x) * DETOUR_STUB,
    y: from.y + Math.sign(towards.y - from.y) * DETOUR_STUB,
  })
  const start = canvasRoute[0]
  const end = canvasRoute[canvasRoute.length - 1]
  const startOut = outward(start, canvasRoute[1])
  const endOut = outward(end, canvasRoute[canvasRoute.length - 2])
  // Only the nodes near the two ends: far ones never shape a detour, but would grow its search
  // grid — and a boxed-in end makes the search visit all of it
  const path = findRectilinearRoute(
    startOut,
    endOut,
    [
      ...obstaclesAround(obstacles.map(toClearedBox), startOut, endOut, DETOUR_MARGIN),
      ...[sourceRect, targetRect].map(toClearedBox),
    ],
    DETOUR_VISITS
  )
  const route = path && simplifyRoute([start, ...path, end])

  return route && isOrthogonalRoute(route) && !routeCrossesAny(route, obstacles) ? route : null
}

/**
 * The preferred route when it clears every other node — the given one (e.g. an exception flow's
 * drop), or else the canvas's own route; otherwise the best clear route over every side pair,
 * from the same router the canvas uses when a user picks a target side, so every candidate is
 * orthogonal and attached by construction. Candidates are ranked by the shared edge router's
 * score: length and bends, and — against `routed`, the routes already drawn — crossings and
 * shared lines, so sequences into one node spread out instead of stacking. When no side pair
 * clears (a node boxed in), a detour is searched through the gaps between the nodes; only when
 * there is none is the canvas's own route kept.
 */
export function routeAvoidingNodes(
  sourceRect: Rect,
  targetRect: Rect,
  obstacles: Rect[],
  preferredRoute?: Point[],
  routed: Point[][] = []
): Point[] {
  const canvasRoute = initialSequencePoints(sourceRect, targetRect)
  const preferred = preferredRoute ?? canvasRoute
  if (isOrthogonalRoute(preferred) && !routeCrossesAny(preferred, obstacles)) {
    return preferred
  }

  const occupied = routed.flatMap((points) => toSegments(points))
  const clear = ROUTE_SIDES.flatMap((sourceSide) =>
    ROUTE_SIDES.map((targetSide) =>
      routeBetweenRectPositions(sourceRect, sourceSide, targetRect, targetSide)
    )
  )
    .filter(isOrthogonalRoute)
    .filter((points) => !routeCrossesAny(points, obstacles))
    // Already clear of every node: only length, bends and the routes drawn so far tell them apart
    .map((points) => ({
      points,
      score: scoreRoute(points, [], occupied, undefined, undefined).score,
    }))

  return clear.length
    ? clear.reduce((best, candidate) => (candidate.score < best.score ? candidate : best)).points
    : (detourAround(canvasRoute, sourceRect, targetRect, obstacles) ?? canvasRoute)
}

/** Applies a plan's node and lane positions to canvas data (routes are derived from these). */
export function withLayout(
  data: CanvasData,
  plan: Pick<ProcessLayoutPlan, 'nodes' | 'swimlanes'>
): CanvasData {
  const nodePositions = new Map(
    plan.nodes.map(({ node, layoutOptions }) => [node.id, layoutOptions])
  )
  const lanePositions = new Map(
    plan.swimlanes.map(({ swimlane, layoutOptions }) => [swimlane.id, layoutOptions])
  )

  return {
    ...data,
    swimlanes: data.swimlanes.map((swimlane) =>
      lanePositions.has(swimlane.id)
        ? { ...swimlane, layoutOptions: lanePositions.get(swimlane.id) }
        : swimlane
    ),
    nodes: data.nodes.map((node) =>
      nodePositions.has(node.id) ? { ...node, layoutOptions: nodePositions.get(node.id) } : node
    ),
  }
}

/**
 * The whole process auto-layout: ELK places nodes across lanes, lanes are packed and restacked,
 * and every sequence gets a fresh route between the new rects. Boundary events are stored
 * relative to their task, so they move with it and need no update. Only what changed is returned.
 */
/**
 * The process without the nodes whose lane no longer exists (with their boundary events and
 * sequences): the lanes cannot hold them, so they keep their place and are routed around as drawn.
 */
function inKnownLanes(data: CanvasData): CanvasData {
  const laneIds = new Set(data.swimlanes.map(({ id }) => id))
  const nodes = data.nodes.filter((node) => laneIds.has(node.swimlaneDefinitionId))
  if (nodes.length === data.nodes.length) {
    return data
  }
  const nodeIds = new Set(nodes.map(({ id }) => id))

  return {
    ...data,
    nodes,
    boundaryEvents: data.boundaryEvents.filter(({ nodeDefinitionId }) =>
      nodeIds.has(nodeDefinitionId)
    ),
    sequences: data.sequences.filter(
      ({ startNodeId, endNodeId }) => nodeIds.has(startNodeId) && nodeIds.has(endNodeId)
    ),
  }
}

export async function planProcessLayout(data: CanvasData): Promise<ProcessLayoutPlan> {
  if (!data.nodes.length) {
    return { nodes: [], swimlanes: [], sequences: [] }
  }

  const laidOut = inKnownLanes(data)
  const { blocks, ranks } = settledExceptionBlocks(laidOut)
  const { positions } = await runElkLayout(
    toProcessElkGraph(laidOut, blocks, ranks),
    processLayoutOptions(laidOut.nodes.length)
  )
  const packed = alignExceptionChildren(
    data,
    packIntoLanes(data, expandExceptionBlocks(positions, blocks)),
    blocks
  )

  return toLayoutPlan(packed, routeAllSequences(withLayout(data, packed), blocks))
}

/** A plan holding only what differs from the current diagram, and only routes the host accepts. */
function toLayoutPlan(
  packed: Pick<ProcessLayoutPlan, 'nodes' | 'swimlanes'>,
  sequences: ProcessLayoutPlan['sequences']
): ProcessLayoutPlan {
  return {
    nodes: packed.nodes.filter(
      ({ node, layoutOptions }) => !samePoint(node.layoutOptions, layoutOptions)
    ),
    swimlanes: packed.swimlanes.filter(
      ({ swimlane, layoutOptions }) => !sameLaneLayout(swimlane.layoutOptions, layoutOptions)
    ),
    sequences: sequences
      // the host rejects non-orthogonal routes; the router's last-resort fallback is unchecked
      .filter(({ points }) => isOrthogonalRoute(points))
      .filter(
        ({ sequence, points }) =>
          !sequence.layoutOptions || !pointsEqual(sequence.layoutOptions, points)
      ),
  }
}

const samePoint = (a: Point | null | undefined, b: Point): boolean => a?.x === b.x && a?.y === b.y

const sameLaneLayout = (a: Partial<LaneLayout> | null | undefined, b: LaneLayout): boolean =>
  a?.x === b.x && a?.y === b.y && a?.width === b.width && a?.height === b.height

/**
 * `data` as it would look once `plan` is saved: node and lane positions and sequence routes. The
 * canvas renders this read-only as the layout preview; nothing is persisted until Apply.
 */
export function applyLayoutPlan(data: CanvasData, plan: ProcessLayoutPlan): CanvasData {
  const arranged = withLayout(data, plan)
  const routes = new Map(plan.sequences.map(({ sequence, points }) => [sequence.id, points]))

  return {
    ...arranged,
    sequences: arranged.sequences.map((sequence) =>
      routes.has(sequence.id) ? { ...sequence, layoutOptions: routes.get(sequence.id) } : sequence
    ),
  }
}

/**
 * Whether `data` already shows everything `plan` changes. After Apply the preview stays up until
 * the live data catches up (lanes are saved first and only land once the API answers), so the
 * canvas never flashes the old layout in between.
 */
export function showsLayoutPlan(data: CanvasData, plan: ProcessLayoutPlan): boolean {
  const nodesById = new Map(data.nodes.map((node) => [node.id, node]))
  const lanesById = new Map(data.swimlanes.map((swimlane) => [swimlane.id, swimlane]))
  const sequencesById = new Map(data.sequences.map((sequence) => [sequence.id, sequence]))

  return (
    plan.nodes.every(({ node, layoutOptions }) =>
      samePoint(nodesById.get(node.id)?.layoutOptions, layoutOptions)
    ) &&
    plan.swimlanes.every(({ swimlane, layoutOptions }) =>
      sameLaneLayout(lanesById.get(swimlane.id)?.layoutOptions, layoutOptions)
    ) &&
    plan.sequences.every(({ sequence, points }) => {
      const current = sequencesById.get(sequence.id)?.layoutOptions

      return isOrthogonalRoute(current) && pointsEqual(current, points)
    })
  )
}
